import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, FolderTree, Hash, Layers, Search, FileSpreadsheet } from 'lucide-react';
import Cookies from 'js-cookie';

/**
 * CategoryReviewDisplay - Shows AI-categorized accounts for Budget Agent
 *
 * Fetches account names from NetSuite API to display proper names
 * instead of just account numbers
 */
const CategoryReviewDisplay = ({ categories, onConfirm, onClose }) => {
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [accountNameMap, setAccountNameMap] = useState({});
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // Fetch account names from API on mount
  useEffect(() => {
    const fetchAccountNames = async () => {
      try {
        setLoadingAccounts(true);
        const token = Cookies.get('sankalvax_token');
        const response = await fetch('/api/budget-agent/expense-accounts', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          // Create a map of account number -> account name
          const nameMap = {};
          (data.accounts || []).forEach(acc => {
            nameMap[acc.acctnumber] = acc.acctname || acc.displayname || acc.acctnumber;
          });
          setAccountNameMap(nameMap);
          console.log('📊 Loaded account names:', Object.keys(nameMap).length);
        }
      } catch (err) {
        console.error('Failed to fetch account names:', err);
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchAccountNames();
  }, []);

  // Get account name from map or return the number
  const getAccountName = (accountNumber) => {
    return accountNameMap[accountNumber] || accountNumber;
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Expand/collapse all
  const expandAll = () => {
    const allIds = filteredCategories.map(c => c.category_id);
    setExpandedCategories(new Set(allIds));
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  // Filter categories by search term
  const filteredCategories = (categories || []).filter(cat => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      cat.category_name?.toLowerCase().includes(term) ||
      cat.category_id?.toLowerCase().includes(term) ||
      cat.account_numbers?.some(num => num.includes(term)) ||
      cat.account_names?.some(name => name?.toLowerCase().includes(term))
    );
  });

  // Get color for category based on index
  const getCategoryColor = (index) => {
    const colors = [
      'border-l-4 border-l-blue-500 bg-white dark:bg-gray-800',
      'border-l-4 border-l-purple-500 bg-white dark:bg-gray-800',
      'border-l-4 border-l-emerald-500 bg-white dark:bg-gray-800',
      'border-l-4 border-l-orange-500 bg-white dark:bg-gray-800',
      'border-l-4 border-l-pink-500 bg-white dark:bg-gray-800',
      'border-l-4 border-l-cyan-500 bg-white dark:bg-gray-800',
      'border-l-4 border-l-amber-500 bg-white dark:bg-gray-800',
      'border-l-4 border-l-indigo-500 bg-white dark:bg-gray-800',
    ];
    return colors[index % colors.length];
  };

  const getDimensionBadgeColor = (dimension) => {
    const colors = {
      subsidiary: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
      department: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
      class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
      location: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
    };
    return colors[dimension] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  };

  // Calculate total accounts
  const totalAccounts = (categories || []).reduce((sum, cat) => {
    return sum + (cat.account_numbers?.filter(n => n !== 'N/A').length || 0);
  }, 0);

  if (!categories || categories.length === 0) {
    return (
      <div className="p-12 text-center">
        <FolderTree className="w-20 h-20 mx-auto text-gray-300 dark:text-gray-600 mb-6" />
        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300">No Categories Found</h3>
        <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-md mx-auto">
          AI categorization did not return any categories. Please try running the categorization again.
        </p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-[98%] max-w-[1800px] max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="p-6 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <FolderTree className="w-5 h-5 text-white" />
              </div>
              Budget Categories
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              Review AI-generated expense account categories
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex items-center gap-6 py-3 px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-indigo-500" />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              <strong className="text-gray-900 dark:text-white">{categories.length}</strong> Categories
            </span>
          </div>
          <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4 text-emerald-500" />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              <strong className="text-gray-900 dark:text-white">{totalAccounts}</strong> Total Accounts
            </span>
          </div>
          {loadingAccounts && (
            <>
              <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
              <span className="text-sm text-blue-500 animate-pulse">Loading account names...</span>
            </>
          )}
        </div>

        {/* Search */}
        <div className="mt-4 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search categories or accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Categories List */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {filteredCategories.map((category, idx) => {
          const isExpanded = expandedCategories.has(category.category_id);
          const accountNumbers = category.account_numbers?.filter(n => n !== 'N/A') || [];
          const accountCount = accountNumbers.length;

          return (
            <div
              key={category.category_id || idx}
              className={`rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden ${getCategoryColor(idx)} transition-all hover:shadow-md`}
            >
              {/* Category Header */}
              <div
                onClick={() => toggleCategory(category.category_id)}
                className="p-5 cursor-pointer flex items-center justify-between bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center">
                    <Layers className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {category.category_name || category.category_id}
                    </h3>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                        <Hash className="w-3.5 h-3.5" />
                        {accountCount} accounts
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                        ID: {category.category_id}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {/* Slicing Dimensions */}
                  {category.suggested_slicing && category.suggested_slicing.length > 0 && (
                    <div className="hidden lg:flex gap-2">
                      {category.suggested_slicing.map((dim, i) => (
                        <span
                          key={i}
                          className={`px-3 py-1 text-xs font-medium rounded-full ${getDimensionBadgeColor(dim)}`}
                        >
                          {dim}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isExpanded ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-5 pb-5 bg-gray-50 dark:bg-gray-800/50">
                  {/* Dimensions (mobile/tablet) */}
                  {category.suggested_slicing && category.suggested_slicing.length > 0 && (
                    <div className="lg:hidden mb-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Budget Dimensions</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {category.suggested_slicing.map((dim, i) => (
                          <span
                            key={i}
                            className={`px-3 py-1 text-xs font-medium rounded-full ${getDimensionBadgeColor(dim)}`}
                          >
                            {dim}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Accounts Table */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                      Accounts in this category
                    </h4>
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide w-24">
                                Number
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                                Account Name
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {accountNumbers.map((num, i) => (
                              <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                <td className="px-4 py-2.5 font-mono text-sm text-gray-500 dark:text-gray-400">
                                  {num}
                                </td>
                                <td className="px-4 py-2.5 text-sm text-gray-800 dark:text-gray-200">
                                  {category.account_names?.[i] || getAccountName(num)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filteredCategories.length === 0 && searchTerm && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No categories match "{searchTerm}"</p>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-6 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {filteredCategories.length} of {categories.length} categories shown
          </p>
          <div className="flex gap-3">
            {onClose && (
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              onClick={() => onConfirm && onConfirm({ action: 'submit' })}
              className="px-8 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30"
            >
              Generate Budget Suggestions
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default CategoryReviewDisplay;
